/**
 * llm-runtime-service.ts
 *
 * Dedicated public service boundary for discrete LLM runtimes.
 * Invariant: This service strictly operates on kind='llm' runtimes and cannot
 * mutate or access Needle 2 worker pool states.
 */

import { ModelRuntime } from '../contracts/model-orchestration-contracts.js';
import { OrchestrationStore } from '../storage/orchestration-store.js';
import { RuntimeRegistry } from './runtime-registry.js';
import { NodeCapabilityRegistry } from '../nodes/node-capability-registry.js';
import { ModelDefinitionRegistry } from '../catalog/model-definition-registry.js';

export class LlmRuntimeService {
  private readonly registry: RuntimeRegistry;

  constructor(
    store: OrchestrationStore,
    nodeRegistry: NodeCapabilityRegistry,
    modelRegistry: ModelDefinitionRegistry
  ) {
    this.registry = new RuntimeRegistry(store, nodeRegistry, modelRegistry);
  }

  async startRuntime(nodeId: string, modelId: string = 'gemma-2-2b-it'): Promise<ModelRuntime> {
    return this.registry.startLlmRuntime(nodeId, modelId);
  }

  async stopRuntime(runtimeId: string, mode: 'drain' | 'terminate' = 'drain'): Promise<void> {
    return this.registry.stopRuntime(runtimeId, mode);
  }

  async forceKillRuntime(runtimeId: string): Promise<void> {
    return this.registry.stopRuntime(runtimeId, 'force_kill');
  }

  async getRuntime(nodeId: string): Promise<ModelRuntime | undefined> {
    const runtimes = await this.registry.getRuntimesForNode(nodeId, 'llm');
    return runtimes.find((r) => r.status === 'active' || r.status === 'starting' || r.status === 'draining') || runtimes[0];
  }

  async getAllLlmRuntimes(): Promise<readonly ModelRuntime[]> {
    const all = await this.registry.getAllRuntimes();
    return all.filter((r) => r.kind === 'llm');
  }
}
